/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { PriceComponent } from '@msdyn365-commerce/components';
import { IAny, ICoreContext, IGeneric, IImageSettings, Image, ITelemetry } from '@msdyn365-commerce/core';
import {
    CommerceListLine,
    ProductPrice,
    ReleasedProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getFallbackImageUrl, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, Button, getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import BmaImage from './bma-image';
import BmaPrice from './bma-price';
import BmaProductAlloySpecification from './bma-productAlloy-specification';
import { IWishlistItemActionMessageState } from '../order-request-list';
import { WishlistItemDimension } from './order-request-list-dimensions';

export interface IWishlistItemProps extends CommerceListLine {
    product: SimpleProduct;
    wishlistState: IWishlistItemActionMessageState;
    price?: ProductPrice;
    context: ICoreContext<IGeneric<IAny>>;
    productUrl: string;
    imageSettings?: IImageSettings;
    removeFromWishlistText: string;
    isInStock: boolean;
    addToCartText: string;
    addToOrderButtonText: string;
    addedButtonText: string;
    addToBMAText: string;
    addToOrderRequestText: string;
    outOfStockText: string;
    index: number;
    itemKey: string;
    telemetry: ITelemetry;
    moduleId: string;
    moduleTypeName: string;
    enableImageProductLink?: boolean;
    inventoryInformationLabel?: string;
    inventoryLabelClassName?: string;
    telemetryContent?: ITelemetryContent;
    handlers: {
        onAddToCart(product: SimpleProduct, itemKey: string): void;
        onAddToOrderRequest(
            product: SimpleProduct,
            price: ProductPrice | undefined,
            snapshotId: string,
            mode: string | undefined,
            event: any,
            itemAlreadyAdded: boolean | undefined
        ): void;
        onRemoveItem(productId: number, snapshotId: string): void | undefined;
        onDismiss(): void;
    };
    resources: {
        productDimensionTypeColor: string;
        productDimensionTypeSize: string;
        productDimensionTypeStyle: string;
        originalPriceText: string;
        currentPriceText: string;
        freePriceText: string;
    };
    snapshotId: string;
    lineId: string;
    addProductLink: any;
    productsCount: number;
    customerInfo: any;
    translationInfo: any;
    addProductsOrderRequest: any;
    customiseButtonText: string;
}

export interface IWishlistItemViewProps {
    key: string;
    productImage: React.ReactNode;
    productLink: React.ReactNode;
    productDimensions?: React.ReactNode[];
    productPrice?: React.ReactNode;
    addToCartButton?: React.ReactNode;
    customiseButton?: React.ReactNode;
    addToOrderRequestButton?: React.ReactNode;
    addToBMAButton?: React.ReactNode;
    addToCartCheckbox?: React.ReactNode;
    addedToOrderButtonComponent?: React.ReactNode;
    removeButton?: React.ReactNode;
    productStatusMessage?: React.ReactNode;
    productName?: React.ReactNode;
    productAriaLabel?: string;
    entireProductLink?: string;
    inventoryInformation?: React.ReactNode;
    productUnitOfMeasure?: React.ReactNode;
    productAlloySpecification: React.ReactNode;
}

const _addItemToCart = (props: IWishlistItemProps): void => {
    const { product, itemKey, handlers } = props;
    const { onAddToCart } = handlers;

    onAddToCart && product && itemKey && onAddToCart(product, itemKey);
};
const _addItemToOrderRequest = (props: IWishlistItemProps, mode?: string, e?: any, itemAlreadyAdded?: boolean): void => {
    const { product, itemKey, handlers, snapshotId, price } = props;
    const { onAddToOrderRequest } = handlers;

    onAddToOrderRequest && product && itemKey && onAddToOrderRequest(product, price, snapshotId, mode, e, itemAlreadyAdded);
};

const _removeItemFromWishlist = (props: IWishlistItemProps): void => {
    const { onRemoveItem } = props.handlers;
    const { RecordId } = props.product;
    const { snapshotId } = props;

    onRemoveItem && RecordId && onRemoveItem(RecordId, snapshotId);
};

const WishListItemActions = {
    addToCart: _addItemToCart,
    addItemToOrderRequest: _addItemToOrderRequest,
    removeItem: _removeItemFromWishlist
};

const _renderAriaLabel = (props: IWishlistItemProps): string => {
    const { product, price, context, resources } = props;

    const { Name, Dimensions } = product;
    const { productDimensionTypeColor, productDimensionTypeSize, productDimensionTypeStyle } = resources;

    let formattedPrice = 'Free';
    let dimensions = null;

    if (price) {
        formattedPrice =
            price.BasePrice === 0 ? resources.freePriceText : context.cultureFormatter.formatCurrency(price.BasePrice!, price.CurrencyCode);
    }

    if (Dimensions) {
        dimensions = Dimensions.map(productDimension => {
            switch (productDimension.DimensionTypeValue) {
                case 1:
                    return `${productDimensionTypeColor}${productDimension.DimensionValue && productDimension.DimensionValue.Value}`;
                case 3:
                    return `${productDimensionTypeSize}${productDimension.DimensionValue && productDimension.DimensionValue.Value}`;
                case 4:
                    return `${productDimensionTypeStyle}${productDimension.DimensionValue && productDimension.DimensionValue.Value}`;
                default:
                    return '';
            }
        });
    }

    return `${Name} ${formattedPrice} ${(dimensions && dimensions.join('')) || ''}`;
};

/**
 * Renders a link to a product based on the given props.
 * @param input - Wishlist item props with the info about the product.
 * @returns React component with a link to a product.
 */
const renderProductLink = (input: IWishlistItemProps) => {
    const { productUrl, telemetryContent } = input;
    const { RecordId: recordId, Name: productName } = input.product;
    const payLoad = getPayloadObject('click', telemetryContent!, '', recordId.toString());
    const productAttributes = getTelemetryAttributes(telemetryContent!, payLoad);
    return (
        !StringExtensions.isNullOrWhitespace(productUrl) && (
            <span className='ms-wishlist-items__product-link' {...productAttributes} aria-label={_renderAriaLabel(input)}>
                {productName}
            </span>
        )
    );
};

/**
 * Renders a product name box.
 * @param productName - The text to display.
 * @returns React component with a name of a product.
 */
const renderProductName = (productName: string) => {
    return <div className='ms-wishlist-items__product-title'>{productName}</div>;
};

/**
 * Renders inventory information.
 * @param inventoryInformationLabel - The text to display.
 * @param inventoryLabelClassName - The component class name.
 * @returns React component.
 */
const renderInventoryInformationLabel = (inventoryInformationLabel: string | undefined, inventoryLabelClassName: string | undefined) => {
    const inventoryCssName = inventoryLabelClassName
        ? `ms-wishlist-items__inventory-label ${inventoryLabelClassName}`
        : 'ms-wishlist-items__inventory-label';
    return (
        !StringExtensions.isNullOrWhitespace(inventoryInformationLabel) && (
            <span className={inventoryCssName}>{inventoryInformationLabel}</span>
        )
    );
};

/**
 * Renders add to cart button.
 * @param input - Wishlist item props with the configuration.
 * @returns React component.
 */
const WishlistItemAddToCartButtonComponent = (input: IWishlistItemProps) => {
    const { isInStock, addToCartText, outOfStockText } = input;

    // Check if the product is service or not by product type.
    const isServiceItem = input.product.ItemTypeValue === ReleasedProductType.Service;

    const addItemAction = React.useCallback(() => {
        WishListItemActions.addToCart(input);
    }, [input]);

    return (
        <Button
            className='ms-wishlist-items__product-add-button'
            title={((isServiceItem || isInStock) && addToCartText) || outOfStockText}
            onClick={addItemAction}
            disabled={!(isServiceItem || isInStock)}
        >
            {((isServiceItem || isInStock) && addToCartText) || outOfStockText}
        </Button>
    );
};

const WishlistItemCustomiseButtonComponent = (input: IWishlistItemProps) => {
    const { product, snapshotId, addProductLink, customiseButtonText, lineId } = input;
    return (
        <Button className='ms-wishlist-items__product-add-button' title={'Customise'} href={addProductLink(product, snapshotId, lineId)}>
            {customiseButtonText}
        </Button>
    );
};
const WishlistItemAddToOrderRequestButtonComponent = (input: IWishlistItemProps) => {
    const { isInStock, addToOrderRequestText, outOfStockText } = input;

    // Check if the product is service or not by product type.
    const isServiceItem = input.product.ItemTypeValue === ReleasedProductType.Service;

    const addItemAction = React.useCallback(() => {
        WishListItemActions.addItemToOrderRequest(input);
    }, [input]);

    return (
        <Button
            style={{ background: '#eee8d6', color: 'black', marginTop: '-38px' }}
            className='ms-wishlist-items__product-add-button'
            title={((isServiceItem || isInStock) && addToOrderRequestText) || outOfStockText}
            onClick={addItemAction}
            disabled={!(isServiceItem || isInStock)}
        >
            {((isServiceItem || isInStock) && addToOrderRequestText) || outOfStockText}
        </Button>
    );
};

const AddedToOrderButtonComponent = (input: IWishlistItemProps) => {
    // const { isInStock, addToOrderRequestText, outOfStockText } = input;
    //
    // // Check if the product is service or not by product type.
    // const isServiceItem = input.product.ItemTypeValue === ReleasedProductType.Service;
    // remove product form list...
    let existsProductIndex: number = -1;
    if (input.snapshotId) {
        // bma products..
        existsProductIndex = input.addProductsOrderRequest.findIndex((p: any) => {
            return p.ProductId === input.product.RecordId && input.snapshotId === p.Snapshotid;
        });
    } else {
        // non - bma products..
        existsProductIndex = input.addProductsOrderRequest.findIndex((p: any) => {
            return p.ProductId === input.product.RecordId;
        });
    }
    const addToOrderRequestButtonText = existsProductIndex > -1 ? input.addedButtonText : input.addToOrderButtonText;

    const addItemAction = React.useCallback(
        (e: any, itemAlreadyAdded: boolean) => {
            WishListItemActions.addItemToOrderRequest(input, 'multiple', e, itemAlreadyAdded);
        },
        [input]
    );
    return (
        <Button
            style={{ marginTop: '-38px' }}
            className='ms-wishlist-items__product-add-button add-to-order-btn'
            title={addToOrderRequestButtonText}
            onClick={(e: any) => addItemAction(e, existsProductIndex > -1)}
        >
            {addToOrderRequestButtonText}
        </Button>
    );
};

// const WishlistItemAddToCartCheckboxComponent = (input: IWishlistItemProps) => {
//     const { isInStock } = input;
//
//     // Check if the product is service or not by product type.
//     const isServiceItem = input.product.ItemTypeValue === ReleasedProductType.Service;
//     const addItemAction = React.useCallback(
//         (e: any) => {
//             WishListItemActions.addItemToOrderRequest(input, 'multiple', e);
//         },
//         [input]
//     );
//
//     return (
//         <input
//             type='checkbox'
//             style={{
//                 height: 24,
//                 fontSize: 0,
//                 outline: 'none',
//                 width: 24,
//                 position: 'absolute',
//                 cursor: 'pointer',
//                 marginTop: 8,
//                 left: 0,
//                 zIndex: 99
//             }}
//             id={input.snapshotId ? input.snapshotId : input.product.RecordId.toString()}
//             name={input.product.ItemId}
//             title={input.snapshotId}
//             value={''}
//             onClick={(e: any) => addItemAction(e)}
//             disabled={!(isServiceItem || isInStock)}
//         />
//     );
// };

/**
 * Renders remove from wishlist button.
 * @param input - Wishlist item props with the configuration.
 * @returns React component.
 */
const WishlistItemRemoveFromWishlistButtonComponent = (input: IWishlistItemProps) => {
    const { removeFromWishlistText } = input;

    const removeItemAction = React.useCallback(() => {
        WishListItemActions.removeItem(input);
    }, [input]);

    return <button className='ms-wishlist-items__product-remove-button' aria-label={removeFromWishlistText} onClick={removeItemAction} />;
};

/**
 * Gets the react node for product unit of measure display.
 * @param  context - Module context.
 * @param unitOfMeasure - Product unit of measure.
 * @returns The node representing markup for unit of measure component.
 */
const renderUnitOfMeasure = (context: ICoreContext<IGeneric<IAny>>, unitOfMeasure?: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
    if (context.app.config && (!context.app.config.unitOfMeasureDisplayType || context.app.config.unitOfMeasureDisplayType === 'none')) {
        return undefined;
    }

    if (!unitOfMeasure) {
        return undefined;
    }

    return (
        <div className='msc-wishlistitems__product-unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
};

export const WishlistItem = (input: IWishlistItemProps): IWishlistItemViewProps | null => {
    const {
        product,
        productUrl,
        price,
        context,
        imageSettings,
        wishlistState,
        handlers,
        inventoryInformationLabel,
        enableImageProductLink,
        inventoryLabelClassName,
        snapshotId,
        productsCount,
        customerInfo,
        translationInfo
    } = input;

    if (!product) {
        return null;
    }
    const { RecordId, Name, PrimaryImageUrl, Dimensions } = product;

    const fallbackImage = getFallbackImageUrl(product.ItemId, context.actionContext.requestContext.apiSettings);

    return {
        key: `${RecordId}-item`,
        productImage:
            PrimaryImageUrl &&
            Name &&
            _renderProductImage(context, product.Name!, PrimaryImageUrl, fallbackImage, imageSettings, snapshotId, productsCount),
        productLink: renderProductLink(input),
        productDimensions: Dimensions && _renderProductDimensions(input),
        productPrice: price && _renderPrice(input, snapshotId, productsCount, customerInfo),
        productUnitOfMeasure: product.DefaultUnitOfMeasure && renderUnitOfMeasure(context, product.DefaultUnitOfMeasure),
        entireProductLink: enableImageProductLink ? productUrl : undefined,
        productAriaLabel: enableImageProductLink ? _renderAriaLabel(input) : undefined,
        productName: enableImageProductLink && Name && renderProductName(Name),

        addToCartButton: !snapshotId && <WishlistItemAddToCartButtonComponent {...input} />,
        customiseButton: snapshotId && <WishlistItemCustomiseButtonComponent {...input} />,
        addToOrderRequestButton: snapshotId && <WishlistItemAddToOrderRequestButtonComponent {...input} />,
        //addToBMAButton: snapshotId && <WishlistItemAddToBMAButtonComponent {...input} />,
        addToBMAButton: null,
        // addToCartCheckbox: <WishlistItemAddToCartCheckboxComponent {...input} />,
        addedToOrderButtonComponent: <AddedToOrderButtonComponent {...input} />,
        inventoryInformation: renderInventoryInformationLabel(inventoryInformationLabel, inventoryLabelClassName),
        removeButton: <WishlistItemRemoveFromWishlistButtonComponent {...input} />,
        productStatusMessage: wishlistState &&
            wishlistState.isOpen &&
            (wishlistState.productId === RecordId || wishlistState.productId === 1234578) && (
                <div className='ms-wishlist-items__product-status'>
                    <Alert color={wishlistState.statusCssString} isOpen={wishlistState.isOpen} toggle={handlers.onDismiss}>
                        <span>{wishlistState.userMessage}</span>
                    </Alert>
                </div>
            ),
        productAlloySpecification: _renderProductSpecification(input, snapshotId, productsCount, translationInfo)
    };
};

const _renderProductImage = (
    context: ICoreContext,
    heading: string,
    image?: string,
    fallbackImage?: string,
    imageSettings?: IImageSettings,
    snapshotId?: string,
    productsCount?: number
): JSX.Element | null => {
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=315&h=315&m=6', w: 0, h: 0 },
            lg: { q: 'w=315&h=315&m=6', w: 0, h: 0 },
            xl: { q: 'w=315&h=315&m=6', w: 0, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    if (snapshotId) {
        return (
            <BmaImage
                requestContext={context.actionContext.requestContext}
                className='ms-wishlist-items__product-image'
                altText={heading}
                title={heading}
                src={''}
                fallBackSrc={fallbackImage}
                gridSettings={context.request.gridSettings!}
                imageSettings={imageSettings || defaultImageSettings}
                loadFailureBehavior='empty'
                snapShotId={snapshotId}
                productsCount={productsCount}
            />
        );
    } else if (image && !snapshotId) {
        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className='ms-wishlist-items__product-image'
                altText={heading}
                title={heading}
                src={image}
                fallBackSrc={fallbackImage}
                gridSettings={context.request.gridSettings!}
                imageSettings={imageSettings || defaultImageSettings}
                loadFailureBehavior='empty'
            />
        );
    }
    return null;
};

const _renderPrice = (props: IWishlistItemProps, snapshotId: string, productsCount: number, customerInfo: any): JSX.Element | null => {
    const { price, context, moduleId, moduleTypeName, resources } = props;

    if (price) {
        if (snapshotId) {
            return (
                <BmaPrice
                    id={moduleId}
                    typeName={moduleTypeName}
                    data={{ price }}
                    className='ms-wishlist-items__product-price'
                    freePriceText={resources.freePriceText}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    context={context}
                    snapShotId={snapshotId}
                    productsCount={productsCount}
                    customerInfo={customerInfo}
                />
            );
        } else {
            return (
                <PriceComponent
                    id={moduleId}
                    typeName={moduleTypeName}
                    data={{ price }}
                    className='ms-wishlist-items__product-price'
                    freePriceText={resources.freePriceText}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    context={context}
                />
            );
        }
    }
    return null;
};
const _renderProductSpecification = (
    props: IWishlistItemProps,
    snapshotId: string,
    productsCount: number,
    translationInfo: any
): JSX.Element | null => {
    const { context, moduleId, moduleTypeName } = props;

    if (snapshotId && translationInfo) {
        return (
            <BmaProductAlloySpecification
                id={moduleId}
                typeName={moduleTypeName}
                context={context}
                snapShotId={snapshotId}
                productsCount={productsCount}
                translationInfo={translationInfo}
            />
        );
    }
    return null;
};

const _renderProductDimensions = (props: IWishlistItemProps): (React.ReactNode | undefined)[] => {
    const { product, resources } = props;

    if (!product || !product.Dimensions) {
        return [];
    }

    return product.Dimensions.map((dimension, index) => {
        if (!dimension.DimensionValue || !dimension.DimensionValue.Value) {
            return undefined;
        }

        let type = '';

        switch (dimension.DimensionTypeValue) {
            case 1: {
                type = resources.productDimensionTypeColor;
                break;
            }
            case 3: {
                type = resources.productDimensionTypeSize;
                break;
            }
            case 4: {
                type = resources.productDimensionTypeStyle;
                break;
            }
            default:
                return undefined;
        }

        return (
            <WishlistItemDimension
                key={`${product.RecordId}-dimensions-${index}`}
                className='msc-price ms-wishlist-items__product-dimension'
                dimensionType={type}
                dimensionValue={dimension.DimensionValue.Value}
            />
        );
    });
};
